import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  ThemeConfig,
  ThemeSettings,
  DEFAULT_THEME,
  PRESET_THEMES,
  SEOConfig,
} from "@/types/theme";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { useTenant } from "@/contexts/TenantContext";
import { fixThemeForegrounds } from "@/utils/themeFixer";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import {
  getContrastingTextColor,
  generateHoverColor,
  getColorContrast,
} from "@/utils/themeUtils";
import { applySEOSettings } from "@/utils/seo";
import { applyDarkModeToTheme } from "@/utils/darkModeUtils";

// Helper function to check if current route is unauthenticated
const isUnauthenticatedRoute = (): boolean => {
  const unauthenticatedPaths = [
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/home",
    "/home-test",
    "/mobile-support",
    "/sign",
    "/welcome-next-steps",
    "/onboarding-booking",
    "/terms",
    "/ai-audit",
  ];
  const currentPath = window.location.pathname;
  return (
    unauthenticatedPaths.includes(currentPath) ||
    currentPath.startsWith("/book/") ||
    currentPath.startsWith("/booking-confirmation/") ||
    currentPath.startsWith("/booking-diagnostic/") ||
    currentPath.startsWith("/ai-audit/")
  );
};

// Helper function to get adaptive sidebar color (copied from ThemeTab)
const getAdaptiveSidebarColor = (
  primary: string,
  background: string
): string => {
  const isLight = getColorContrast(background) === "light";

  if (isLight) {
    // For light backgrounds, create a dark sidebar that complements the primary color
    const primaryIsDark = getColorContrast(primary) === "dark";

    if (primaryIsDark) {
      // If primary is dark, use it as the sidebar color
      return primary;
    } else {
      // If primary is light, use a dark neutral color
      return "#1f2937"; // Dark gray that works well with most themes
    }
  } else {
    // For dark backgrounds, use a lighter sidebar
    const primaryIsLight = getColorContrast(primary) === "light";

    if (primaryIsLight) {
      // If primary is light, use it as the sidebar color
      return primary;
    } else {
      // If primary is dark, use a light neutral color
      return "#f3f4f6"; // Light gray that works well with most themes
    }
  }
};

interface ThemeContextType {
  currentTheme: ThemeConfig;
  themeSettings: ThemeSettings;
  setTheme: (themeId: string) => Promise<void>;
  updateCustomLogo: (logoUrl: string) => Promise<void>;
  updateCustomFavicon: (faviconUrl: string) => Promise<void>;
  updateCustomSEO: (seoSettings: SEOConfig) => Promise<void>;
  createCustomTheme: (
    theme: Omit<ThemeConfig, "id" | "createdAt">
  ) => Promise<void>;
  updateCustomTheme: (
    themeId: string,
    updates: Partial<ThemeConfig>
  ) => Promise<void>;
  deleteCustomTheme: (themeId: string) => Promise<void>;
  updateSidebarColor: (color: string) => Promise<void>;
  applyTheme: (theme: ThemeConfig) => void;
  toggleDarkMode: () => Promise<void>;
  isDarkMode: boolean;
  isLoading: boolean;
  canManageTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>({
    currentTheme: DEFAULT_THEME.id,
    themes: PRESET_THEMES,
    customThemes: [],
    sidebarColorOverride: null,
    darkMode: false, // This will be overridden by user preferences
  });
  const [userDarkMode, setUserDarkMode] = useState(false); // User-specific dark mode preference
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [canManageTheme, setCanManageTheme] = useState(false);
  
  // Refs to track current values for stable history API override
  const currentThemeRef = useRef(themeSettings.currentTheme);
  const isLoadingRef = useRef(isLoading);
  const userDarkModeRef = useRef(userDarkMode);
  
  const {
    hasPermission,
    loading: permissionLoading,
    permissions,
  } = usePermissions();
  const { session, loading: authLoading, isImpersonating } = useAuth();
  const { currentTenant } = useTenant();
  
  // Initialize session ref after session is declared
  const sessionRef = useRef(session);
  
  // Update refs when values change
  useEffect(() => {
    currentThemeRef.current = themeSettings.currentTheme;
  }, [themeSettings.currentTheme]);
  
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  
  useEffect(() => {
    userDarkModeRef.current = userDarkMode;
  }, [userDarkMode]);
  
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // Watch for permission changes and update canManageTheme
  useEffect(() => {
    if (!permissionLoading) {
      const hasThemePermissions =
        hasPermission("settings.admin") ||
        hasPermission("manage_roles") ||
        hasPermission("manage_users") ||
        hasPermission("system.override");
      setCanManageTheme(hasThemePermissions);
    } else {
      setCanManageTheme(false);
    }
  }, [permissionLoading, hasPermission, permissions]);

  const baseTheme =
    [...themeSettings.themes, ...themeSettings.customThemes].find(
      (t) => t.id === themeSettings.currentTheme
    ) || DEFAULT_THEME;

  // Apply custom logo and favicon to base theme
  let themeWithCustomAssets = baseTheme;
  if (themeSettings.customLogo) {
    themeWithCustomAssets = {
      ...themeWithCustomAssets,
      logo: themeSettings.customLogo,
    };
  }
  if (themeSettings.customFavicon) {
    themeWithCustomAssets = {
      ...themeWithCustomAssets,
      favicon: themeSettings.customFavicon,
    };
  }

  // Apply sidebar color override if it exists and the theme is not custom
  const currentTheme = fixThemeForegrounds(
    themeSettings.sidebarColorOverride && !baseTheme.isCustom
      ? {
          ...themeWithCustomAssets,
          colors: {
            ...themeWithCustomAssets.colors,
            sidebarColor: themeSettings.sidebarColorOverride,
            sidebarTextColor: getContrastingTextColor(
              themeSettings.sidebarColorOverride
            ),
            sidebarHoverBgColor: generateHoverColor(
              themeSettings.sidebarColorOverride,
              0.15
            ),
            sidebarHoverTextColor: getContrastingTextColor(
              generateHoverColor(themeSettings.sidebarColorOverride, 0.15)
            ),
          },
        }
      : themeWithCustomAssets
  );

  // Apply theme to CSS custom properties
  const applyTheme = (theme: ThemeConfig) => {
    // Fix theme foregrounds before applying
    let fixedTheme = fixThemeForegrounds(theme);

    // Apply dark mode colors if user's dark mode is enabled and user is authenticated and not on unauthenticated route
    if (userDarkMode && session && !isUnauthenticatedRoute()) {
      fixedTheme = {
        ...fixedTheme,
        colors: applyDarkModeToTheme(fixedTheme.colors, true),
      };
    }

    const root = document.documentElement;

    // Apply dark mode class for any remaining Tailwind dark: utilities
    // Only apply dark mode if user's dark mode is enabled and user is authenticated and not on unauthenticated route
    if (userDarkMode && session && !isUnauthenticatedRoute()) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    // Apply color variables
    Object.entries(fixedTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    // Apply logo and favicon
    if (fixedTheme.logo) {
      root.style.setProperty("--logo-url", `url(${fixedTheme.logo})`);
    }
    if (fixedTheme.favicon) {
      root.style.setProperty("--favicon-url", `url(${fixedTheme.favicon})`);
    }

    // Apply sidebar colors
    if (fixedTheme.colors.sidebarColor) {
      root.style.setProperty("--sidebar-color", fixedTheme.colors.sidebarColor);
    }
    if (fixedTheme.colors.sidebarTextColor) {
      root.style.setProperty(
        "--sidebar-text-color",
        fixedTheme.colors.sidebarTextColor
      );
    }
    if (fixedTheme.colors.sidebarHoverBgColor) {
      root.style.setProperty(
        "--sidebar-hover-bg-color",
        fixedTheme.colors.sidebarHoverBgColor
      );
    }
    if (fixedTheme.colors.sidebarHoverTextColor) {
      root.style.setProperty(
        "--sidebar-hover-text-color",
        fixedTheme.colors.sidebarHoverTextColor
      );
    }

    // Update favicon in head
    const faviconLink = document.querySelector(
      'link[rel="icon"]'
    ) as HTMLLinkElement;
    if (faviconLink && fixedTheme.favicon) {
      faviconLink.href = fixedTheme.favicon;
    }

    // SEO will be applied after theme is fully loaded to prevent flickering
  };

  // Load user preferences from database
  const loadUserPreferences = async () => {
    try {
      if (!session) {
        console.warn("No session found when loading user preferences");
        return;
      }

      const { data, error } = await supabase
        .from("user_preferences")
        .select("dark_mode")
        .eq("user_id", session.user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        // PGRST116 = no rows returned
        console.error("Error loading user preferences:", error);
        return;
      }

      if (data) {
        setUserDarkMode(data.dark_mode || false);
      }
    } catch (error) {
      console.error("Error loading user preferences:", error);
    }
  };

  // Save user preferences to database
  const saveUserPreferences = async (darkMode: boolean) => {
    try {
      if (!session) {
        console.warn("No session found when saving user preferences");
        return;
      }

      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: session.user.id,
          dark_mode: darkMode,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id",
        }
      );

      if (error) {
        console.error("Error saving user preferences:", error);
      }
    } catch (error) {
      console.error("Error saving user preferences:", error);
    }
  };

  // Load theme settings from database
  const loadThemeSettings = async () => {
    try {
      // Skip theme loading if modal is open to prevent interference
      if ((window as any).__SOLERRO_MODAL_OPEN) {
        return;
      }
      
      // Use the session from AuthContext instead of fetching it again
      if (!session) {
        console.warn("No session found when loading theme settings");
        setIsLoading(false);
        return;
      }

      // Force fresh data by adding a timestamp parameter (prevents caching)
      const { data, error } = await supabase.rpc("get_theme_setting");

      if (error) {
        console.error("Error loading theme settings:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
      }

      if (data && data.current_theme) {
        // Parse custom themes from JSON
        let customThemes = [];
        if (data.custom_themes && Array.isArray(data.custom_themes)) {
          customThemes = data.custom_themes;
        }

        const newSettings = {
          currentTheme: data.current_theme || DEFAULT_THEME.id,
          customLogo: data.custom_logo,
          customFavicon: data.custom_favicon,
          customSEO: data.custom_seo || null,
          themes: PRESET_THEMES,
          customThemes: customThemes,
          sidebarColorOverride: data.sidebar_color_override || null,
          darkMode: false, // No longer used - dark mode is now user-specific
        };
        setThemeSettings(newSettings);

        // Apply the theme immediately after loading settings
        let themeToApply =
          [...PRESET_THEMES, ...customThemes].find(
            (t) => t.id === newSettings.currentTheme
          ) || DEFAULT_THEME;

        // Apply custom logo and favicon if they exist

        if (newSettings.customLogo) {
          themeToApply = {
            ...themeToApply,
            logo: newSettings.customLogo,
          };
        }
        if (newSettings.customFavicon) {
          themeToApply = {
            ...themeToApply,
            favicon: newSettings.customFavicon,
          };
        }

        // Apply sidebar color override if it exists and the theme is not custom
        if (newSettings.sidebarColorOverride && !themeToApply.isCustom) {
          themeToApply = {
            ...themeToApply,
            colors: {
              ...themeToApply.colors,
              sidebarColor: newSettings.sidebarColorOverride,
              sidebarTextColor: getContrastingTextColor(
                newSettings.sidebarColorOverride
              ),
              sidebarHoverBgColor: generateHoverColor(
                newSettings.sidebarColorOverride,
                0.15
              ),
              sidebarHoverTextColor: getContrastingTextColor(
                generateHoverColor(newSettings.sidebarColorOverride, 0.15)
              ),
            },
          };
        }


        applyTheme(themeToApply);

        // Apply SEO settings after theme is applied - only if tenant context is loaded
        if (currentTenant !== undefined) {
          if (newSettings.customSEO) {
            applySEOSettings(
              newSettings.customSEO,
              currentTenant?.company_name
            );
          } else if (themeToApply.seo) {
            applySEOSettings(themeToApply.seo, currentTenant?.company_name);
          } else {
            applySEOSettings(null, currentTenant?.company_name);
          }
        }
      } else {
        // No saved settings found, apply default theme
        applyTheme(DEFAULT_THEME);

        // Apply tenant-aware fallback SEO - only if tenant context is loaded
        if (currentTenant !== undefined) {
          if (DEFAULT_THEME.seo) {
            applySEOSettings(DEFAULT_THEME.seo, currentTenant?.company_name);
          } else {
            applySEOSettings(null, currentTenant?.company_name);
          }
        }
      }
    } catch (error) {
      console.error("Error loading theme settings:", error);
      // Apply default theme on error
      applyTheme(DEFAULT_THEME);

      // Apply tenant-aware fallback SEO on error - only if tenant context is loaded
      if (currentTenant !== undefined) {
        if (DEFAULT_THEME.seo) {
          applySEOSettings(DEFAULT_THEME.seo, currentTenant?.company_name);
        } else {
          applySEOSettings(null, currentTenant?.company_name);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Save theme settings to database
  const saveThemeSettings = async (settings: Partial<ThemeSettings>) => {
    try {
      // Use the session from AuthContext instead of fetching it again
      if (!session) {
        console.warn("No session found when saving theme settings");
        return;
      }

      setIsSaving(true);

      // Try using the database function first (excluding dark_mode parameter - now user-specific)
      let { data, error } = await supabase.rpc("upsert_theme_setting", {
        p_current_theme: settings.currentTheme,
        p_custom_logo: settings.customLogo,
        p_custom_favicon: settings.customFavicon,
        p_custom_themes: settings.customThemes || [],
        p_sidebar_color_override: settings.sidebarColorOverride || null,
        p_custom_seo: settings.customSEO || null,
      });

      // If function related errors occur, fall back to direct table access
      // PGRST203: function overloading conflict
      // 42883: function does not exist
      // 42703: column doesn't exist
      if (
        error &&
        (error.code === "PGRST203" ||
          error.code === "42883" ||
          error.code === "42703" ||
          error.message?.includes("function") ||
          error.message?.includes("does not exist"))
      ) {
        console.warn(
          `Database function issue detected (${error.code}: ${error.message}), falling back to direct table access`
        );

        // Get tenant ID from TenantContext instead of cached utility to prevent cross-tenant contamination
        const tenantId = currentTenant?.tenant_id;
        if (!tenantId) {
          console.error("No tenant ID available for saving theme settings");
          return;
        }

        const { data: fallbackData, error: fallbackError } = await supabase
          .from("tenant_theme_settings")
          .upsert(
            {
              tenant_id: tenantId,
              current_theme: settings.currentTheme,
              custom_logo: settings.customLogo,
              custom_favicon: settings.customFavicon,
              custom_themes: settings.customThemes || [],
              sidebar_color_override: settings.sidebarColorOverride || null,
              custom_seo: settings.customSEO || null,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "tenant_id",
            }
          )
          .select()
          .single();

        data = fallbackData;
        error = fallbackError;
      }

      if (error) {
        console.error("Error saving theme settings:", error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
      } else {
      }
    } catch (error) {
      console.error("Error saving theme settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Set theme
  const setTheme = async (themeId: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can change theme settings");
      return;
    }

    // Look for theme in both preset themes and custom themes
    const theme = [...themeSettings.themes, ...themeSettings.customThemes].find(
      (t) => t.id === themeId
    );
    if (!theme) {
      console.warn(`Theme with id ${themeId} not found`);
      return;
    }

    // Clear sidebar color override when switching themes (for preset themes)
    const newSettings = {
      ...themeSettings,
      currentTheme: themeId,
      sidebarColorOverride: theme.isCustom
        ? themeSettings.sidebarColorOverride
        : null,
    };
    setThemeSettings(newSettings);
    applyTheme(theme);
    await saveThemeSettings(newSettings);
  };

  // Update custom logo
  const updateCustomLogo = async (logoUrl: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update logo settings");
      return;
    }

    const newSettings = { ...themeSettings, customLogo: logoUrl };
    setThemeSettings(newSettings);

    // Apply custom logo to current theme
    const themeWithCustomLogo = { ...currentTheme, logo: logoUrl };
    applyTheme(themeWithCustomLogo);

    await saveThemeSettings(newSettings);
  };

  // Update custom favicon
  const updateCustomFavicon = async (faviconUrl: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update favicon settings");
      return;
    }

    const newSettings = { ...themeSettings, customFavicon: faviconUrl };
    setThemeSettings(newSettings);

    // Apply custom favicon to current theme
    const themeWithCustomFavicon = { ...currentTheme, favicon: faviconUrl };
    applyTheme(themeWithCustomFavicon);

    await saveThemeSettings(newSettings);
  };

  // Update custom SEO settings
  const updateCustomSEO = async (seoSettings: SEOConfig) => {
    if (!canManageTheme) {
      console.warn("Only admins can update SEO settings");
      return;
    }

    const newSettings = { ...themeSettings, customSEO: seoSettings };
    setThemeSettings(newSettings);

    // Apply SEO settings immediately
    applySEOSettings(seoSettings, currentTenant?.company_name);

    await saveThemeSettings(newSettings);
  };

  // Create custom theme
  const createCustomTheme = async (
    theme: Omit<ThemeConfig, "id" | "createdAt">
  ) => {
    if (!canManageTheme) {
      console.warn("Only admins can create custom themes");
      return;
    }

    const newTheme: ThemeConfig = {
      ...theme,
      id: `custom-${Date.now()}`,
      isCustom: true,
      createdAt: new Date().toISOString(),
    };

    const newSettings = {
      ...themeSettings,
      customThemes: [...themeSettings.customThemes, newTheme],
    };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  // Update custom theme
  const updateCustomTheme = async (
    themeId: string,
    updates: Partial<ThemeConfig>
  ) => {
    if (!canManageTheme) {
      console.warn("Only admins can update custom themes");
      return;
    }

    const existingTheme = themeSettings.customThemes.find(
      (t) => t.id === themeId
    );
    if (!existingTheme) {
      console.warn(`Custom theme with id ${themeId} not found`);
      return;
    }

    const updatedTheme: ThemeConfig = { ...existingTheme, ...updates };
    const newSettings: ThemeSettings = {
      ...themeSettings,
      customThemes: themeSettings.customThemes.map((theme) =>
        theme.id === themeId ? updatedTheme : theme
      ),
    };
    setThemeSettings(newSettings);

    // If we're updating the currently active theme, apply it immediately
    if (themeSettings.currentTheme === themeId) {
      applyTheme(updatedTheme);
    }

    await saveThemeSettings(newSettings);
  };

  // Delete custom theme
  const deleteCustomTheme = async (themeId: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can delete custom themes");
      return;
    }

    const newSettings = {
      ...themeSettings,
      customThemes: themeSettings.customThemes.filter(
        (theme) => theme.id !== themeId
      ),
    };
    setThemeSettings(newSettings);
    await saveThemeSettings(newSettings);
  };

  // Update sidebar color for any theme (preset or custom)
  const updateSidebarColor = async (color: string) => {
    if (!canManageTheme) {
      console.warn("Only admins can update sidebar colors");
      return;
    }

    // Check if this is the default theme-adaptive color (no override needed)
    const baseTheme =
      [...themeSettings.themes, ...themeSettings.customThemes].find(
        (t) => t.id === themeSettings.currentTheme
      ) || DEFAULT_THEME;
    const defaultAdaptiveColor = getAdaptiveSidebarColor(
      baseTheme.colors.primary,
      baseTheme.colors.background
    );

    // Create a modified version of the current theme with the new sidebar color
    const updatedTheme = {
      ...currentTheme,
      colors: {
        ...currentTheme.colors,
        sidebarColor: color,
        sidebarTextColor: getContrastingTextColor(color),
        sidebarHoverBgColor: generateHoverColor(color, 0.15),
        sidebarHoverTextColor: getContrastingTextColor(
          generateHoverColor(color, 0.15)
        ),
      },
    };

    // Apply the updated theme immediately
    applyTheme(updatedTheme);

    // If it's a custom theme, update it in the database
    if (currentTheme.isCustom) {
      await updateCustomTheme(currentTheme.id, updatedTheme);
    } else {
      // For preset themes, save the sidebar color override (or clear it if using default)
      const newSettings = {
        ...themeSettings,
        currentTheme: currentTheme.id,
        sidebarColorOverride: color === defaultAdaptiveColor ? null : color,
      };
      setThemeSettings(newSettings);
      await saveThemeSettings(newSettings);
    }
  };

  // Toggle dark mode (user-specific)
  const toggleDarkMode = async () => {
    const newDarkMode = !userDarkMode;
    setUserDarkMode(newDarkMode);

    // Apply the current theme with new dark mode setting
    applyTheme(currentTheme);

    // Don't save to database during impersonation to avoid tenant/permission issues
    // Dark mode will revert when impersonation ends, but works during the session
    if (!isImpersonating) {
      // Save to user preferences (not tenant settings)
      await saveUserPreferences(newDarkMode);
    }
  };

  // Load settings when session is available or tenant changes
  useEffect(() => {
    // Don't reload if modal is open
    if ((window as any).__SOLERRO_MODAL_OPEN) {
      return;
    }
    
    // Don't reload if we're currently saving to prevent overriding local state
    if (isSaving) {
      return;
    }

    // Don't reload if tenant is undefined (still loading)
    if (currentTenant === undefined) {
      return;
    }

    if (!authLoading && session) {
      // Check if we're on ai-audit routes - use domain tenant theme even for authenticated users
      const currentPath = window.location.pathname;
      const isAiAuditRoute = currentPath.startsWith('/ai-audit');
      
      if (isAiAuditRoute) {
        const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
        if (initialDomainTenant) {
          try {
            const domainTenant = JSON.parse(initialDomainTenant);
            if (domainTenant?.theme) {
              
              // Create theme settings from domain tenant data
              const domainThemeSettings = {
                currentTheme: domainTenant.theme.currentTheme || DEFAULT_THEME.id,
                customLogo: domainTenant.theme.customLogo,
                customFavicon: domainTenant.theme.customFavicon,
                customSEO: domainTenant.theme.customSEO,
                themes: PRESET_THEMES,
                customThemes: domainTenant.theme.customThemes || [],
                sidebarColorOverride: domainTenant.theme.sidebarColorOverride,
                darkMode: false
              };
              
              setThemeSettings(domainThemeSettings);
              
              // Find the correct theme to apply
              let themeToApply = PRESET_THEMES.find(t => t.id === domainTenant.theme.currentTheme);
              
              // Check custom themes if not found in presets
              if (!themeToApply && domainTenant.theme.customThemes) {
                themeToApply = domainTenant.theme.customThemes.find((t: any) => t.id === domainTenant.theme.currentTheme);
              }
              
              // Fallback to default theme
              if (!themeToApply) {
                themeToApply = DEFAULT_THEME;
              }
              
              // Apply custom logo and favicon if available
              if (domainTenant.theme.customLogo) {
                themeToApply = { ...themeToApply, logo: domainTenant.theme.customLogo };
              }
              if (domainTenant.theme.customFavicon) {
                themeToApply = { ...themeToApply, favicon: domainTenant.theme.customFavicon };
              }
              
              applyTheme(themeToApply);
              
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse initial domain tenant for authenticated user:', e);
          }
        }
      }
      
      loadThemeSettings();
      loadUserPreferences(); // Load user's dark mode preference
    } else if (!authLoading && !session) {
      // Check for pre-loaded domain tenant theme for unauthenticated users
      const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
      if (initialDomainTenant) {
        try {
          const domainTenant = JSON.parse(initialDomainTenant);
          if (domainTenant?.theme) {
            // Apply the domain tenant theme properly instead of just preserving it
            
            // Create theme settings from domain tenant data
            const domainThemeSettings = {
              currentTheme: domainTenant.theme.currentTheme || DEFAULT_THEME.id,
              customLogo: domainTenant.theme.customLogo,
              customFavicon: domainTenant.theme.customFavicon,
              customSEO: domainTenant.theme.customSEO,
              themes: PRESET_THEMES,
              customThemes: domainTenant.theme.customThemes || [],
              sidebarColorOverride: domainTenant.theme.sidebarColorOverride,
              darkMode: false
            };
            
            setThemeSettings(domainThemeSettings);
            
            // Find the correct theme to apply
            let themeToApply = PRESET_THEMES.find(t => t.id === domainTenant.theme.currentTheme);
            
            // Check custom themes if not found in presets
            if (!themeToApply && domainTenant.theme.customThemes) {
              themeToApply = domainTenant.theme.customThemes.find((t: any) => t.id === domainTenant.theme.currentTheme);
            }
            
            // Fallback to default theme
            if (!themeToApply) {
              themeToApply = DEFAULT_THEME;
            }
            
            // Apply custom logo and favicon if available
            if (domainTenant.theme.customLogo) {
              themeToApply = { ...themeToApply, logo: domainTenant.theme.customLogo };
            }
            if (domainTenant.theme.customFavicon) {
              themeToApply = { ...themeToApply, favicon: domainTenant.theme.customFavicon };
            }
            
            applyTheme(themeToApply);
            
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.warn('Failed to parse initial domain tenant:', e);
          sessionStorage.removeItem('initial_domain_tenant');
        }
      }
      
      setIsLoading(false);
      applyTheme(DEFAULT_THEME);
    }
  }, [session, authLoading, isSaving, currentTenant]);

  // Apply theme when current theme or user dark mode changes
  useEffect(() => {
    // Skip theme changes if modal is open to prevent interference
    if ((window as any).__SOLERRO_MODAL_OPEN) {
      return;
    }
    
    if (!isLoading) {
      // Don't override domain theme for unauthenticated users
      if (!session) {
        const initialDomainTenant = sessionStorage.getItem('initial_domain_tenant');
        if (initialDomainTenant) {
          try {
            const domainTenant = JSON.parse(initialDomainTenant);
            if (domainTenant?.theme) {
              return;
            }
          } catch (e) {
            // Continue with normal theme application
          }
        }
      }
      
      applyTheme(currentTheme);
    }
  }, [currentTheme, isLoading, userDarkMode, session]);

  // Listen for route changes to reapply theme when navigating between authenticated/unauthenticated routes
  useEffect(() => {
    let isOverridden = false;
    
    const handleLocationChange = () => {
      // Skip theme changes if modal is open to prevent interference
      if ((window as any).__SOLERRO_MODAL_OPEN) {
        return;
      }
      
      // Only apply theme changes when navigating between authenticated/unauthenticated routes
      // Skip for regular navigation within the app to prevent theme flashing
      const isUnauthRoute = isUnauthenticatedRoute();
      const wasUnauthRoute = sessionStorage.getItem('lastRouteWasUnauth') === 'true';
      
      // Store current route type for next check
      sessionStorage.setItem('lastRouteWasUnauth', isUnauthRoute.toString());
      
      // Only proceed if transitioning between auth states
      if (isUnauthRoute === wasUnauthRoute) {
        return;
      }
      
      if (!isLoadingRef.current) {
        // Get the base theme
        const baseTheme = [...themeSettings.themes, ...themeSettings.customThemes].find(
          (t) => t.id === currentThemeRef.current
        ) || DEFAULT_THEME;
        
        // Apply custom logo and favicon to base theme if they exist
        let themeWithCustomAssets = baseTheme;
        if (themeSettings.customLogo) {
          themeWithCustomAssets = {
            ...themeWithCustomAssets,
            logo: themeSettings.customLogo,
          };
        }
        if (themeSettings.customFavicon) {
          themeWithCustomAssets = {
            ...themeWithCustomAssets,
            favicon: themeSettings.customFavicon,
          };
        }

        // Apply sidebar color override if it exists and the theme is not custom
        const finalTheme = themeSettings.sidebarColorOverride && !baseTheme.isCustom
          ? {
              ...themeWithCustomAssets,
              colors: {
                ...themeWithCustomAssets.colors,
                sidebarColor: themeSettings.sidebarColorOverride,
                sidebarTextColor: getContrastingTextColor(
                  themeSettings.sidebarColorOverride
                ),
                sidebarHoverBgColor: generateHoverColor(
                  themeSettings.sidebarColorOverride,
                  0.15
                ),
                sidebarHoverTextColor: getContrastingTextColor(
                  generateHoverColor(themeSettings.sidebarColorOverride, 0.15)
                ),
              },
            }
          : themeWithCustomAssets;

        // Use the current theme with all modifications and preserve user's dark mode setting
        const themeToApply = fixThemeForegrounds(finalTheme);
        
        // Apply dark mode colors if user's dark mode is enabled
        if (userDarkModeRef.current && sessionRef.current && !isUnauthenticatedRoute()) {
          const darkModeTheme = {
            ...themeToApply,
            colors: applyDarkModeToTheme(themeToApply.colors, true),
          };
          applyTheme(darkModeTheme);
        } else {
          applyTheme(themeToApply);
        }
      }
    };

    // Only set up listeners if not already done
    if (!isOverridden) {
      // Listen for both popstate (browser back/forward) and pushstate/replacestate (programmatic navigation)
      window.addEventListener("popstate", handleLocationChange);

      // Store original functions to avoid multiple overrides
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function (...args) {
        originalPushState.apply(this, args);
        setTimeout(handleLocationChange, 0); // Use setTimeout to avoid immediate execution
      };

      history.replaceState = function (...args) {
        originalReplaceState.apply(this, args);
        setTimeout(handleLocationChange, 0);
      };

      isOverridden = true;

      return () => {
        window.removeEventListener("popstate", handleLocationChange);
        // Only restore if we actually overrode them
        if (isOverridden) {
          history.pushState = originalPushState;
          history.replaceState = originalReplaceState;
        }
      };
    }
  }, []); // Keep empty to prevent multiple overrides

  // Apply SEO when tenant becomes available and theme is loaded
  useEffect(() => {
    if (!isLoading && currentTenant !== undefined) {
      // Apply SEO in priority order: custom SEO > theme SEO > tenant fallback
      if (themeSettings.customSEO) {
        applySEOSettings(themeSettings.customSEO, currentTenant?.company_name);
      } else if (currentTheme.seo) {
        applySEOSettings(currentTheme.seo, currentTenant?.company_name);
      } else {
        applySEOSettings(null, currentTenant?.company_name);
      }
    }
  }, [currentTenant, isLoading, themeSettings.customSEO, currentTheme.seo]);

  const value: ThemeContextType = useMemo(() => ({
    currentTheme,
    themeSettings,
    setTheme,
    updateCustomLogo,
    updateCustomFavicon,
    updateCustomSEO,
    createCustomTheme,
    updateCustomTheme,
    deleteCustomTheme,
    updateSidebarColor,
    applyTheme,
    toggleDarkMode,
    isDarkMode: userDarkMode,
    isLoading,
    canManageTheme,
  }), [currentTheme, themeSettings, setTheme, updateCustomLogo, updateCustomFavicon, updateCustomSEO, createCustomTheme, updateCustomTheme, deleteCustomTheme, updateSidebarColor, applyTheme, toggleDarkMode, userDarkMode, isLoading, canManageTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
